/**
 * React Query hooks for Ahrefs Keyword Intelligence API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ahrefsService,
  KeywordResearchRequest,
  KeywordResearchResponse,
  KeywordContextRequest,
  KeywordContextResponse,
  CompetitorKeywordsRequest,
  CompetitorKeywordsResponse,
  ContentGapRequest,
  ContentGapResponse,
  TopPageRequest,
  TopPagesResponse,
} from '@/services/ahrefsService';

// Query keys
export const ahrefsKeys = {
  all: ['ahrefs'] as const,
  keywordResearch: (seed: string, country?: string) =>
    [...ahrefsKeys.all, 'keyword-research', seed, country] as const,
  keywordContext: (keyword: string, country?: string) =>
    [...ahrefsKeys.all, 'keyword-context', keyword, country] as const,
  competitorKeywords: (target: string, country?: string) =>
    [...ahrefsKeys.all, 'competitor-keywords', target, country] as const,
  contentGap: (yourDomain: string, competitors: string[]) =>
    [...ahrefsKeys.all, 'content-gap', yourDomain, competitors.join(',')] as const,
  topPages: (target: string, country?: string) =>
    [...ahrefsKeys.all, 'top-pages', target, country] as const,
};

/**
 * Hook for keyword research
 */
export function useKeywordResearch(
  request: KeywordResearchRequest | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ahrefsKeys.keywordResearch(request?.seed_keyword || '', request?.country),
    queryFn: () => ahrefsService.researchKeywords(request!),
    enabled: !!request?.seed_keyword && options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Mutation for keyword research (on-demand)
 */
export function useKeywordResearchMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: KeywordResearchRequest) =>
      ahrefsService.researchKeywords(request),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ahrefsKeys.keywordResearch(variables.seed_keyword, variables.country),
        data
      );
    },
  });
}

/**
 * Hook for keyword context
 */
export function useKeywordContext(
  request: KeywordContextRequest | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ahrefsKeys.keywordContext(request?.keyword || '', request?.country),
    queryFn: () => ahrefsService.getKeywordContext(request!),
    enabled: !!request?.keyword && options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation for keyword context (on-demand)
 */
export function useKeywordContextMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: KeywordContextRequest) =>
      ahrefsService.getKeywordContext(request),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ahrefsKeys.keywordContext(variables.keyword, variables.country),
        data
      );
    },
  });
}

/**
 * Hook for competitor keywords
 */
export function useCompetitorKeywords(
  request: CompetitorKeywordsRequest | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ahrefsKeys.competitorKeywords(request?.target_url || '', request?.country),
    queryFn: () => ahrefsService.getCompetitorKeywords(request!),
    enabled: !!request?.target_url && options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation for competitor keywords (on-demand)
 */
export function useCompetitorKeywordsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CompetitorKeywordsRequest) =>
      ahrefsService.getCompetitorKeywords(request),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ahrefsKeys.competitorKeywords(variables.target_url, variables.country),
        data
      );
    },
  });
}

/**
 * Hook for content gap analysis
 */
export function useContentGap(
  request: ContentGapRequest | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ahrefsKeys.contentGap(
      request?.your_domain || '',
      request?.competitor_domains || []
    ),
    queryFn: () => ahrefsService.getContentGap(request!),
    enabled:
      !!request?.your_domain &&
      request.competitor_domains?.length > 0 &&
      options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation for content gap (on-demand)
 */
export function useContentGapMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ContentGapRequest) =>
      ahrefsService.getContentGap(request),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ahrefsKeys.contentGap(variables.your_domain, variables.competitor_domains),
        data
      );
    },
  });
}

/**
 * Hook for top pages
 */
export function useTopPages(
  request: TopPageRequest | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ahrefsKeys.topPages(request?.target || '', request?.country),
    queryFn: () => ahrefsService.getTopPages(request!),
    enabled: !!request?.target && options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation for top pages (on-demand)
 */
export function useTopPagesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: TopPageRequest) =>
      ahrefsService.getTopPages(request),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ahrefsKeys.topPages(variables.target, variables.country),
        data
      );
    },
  });
}
